﻿import React from "react";
import { useAppSelector } from "../../../store";
import { useAppActions } from "../../../shared/hooks";

import { Dialog, DialogTitleWithCloseButton } from "../../../shared/components";
import { Breakpoint } from "@mui/material";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { TooltipContainerContext } from "../../../shared/providers/TooltipContainerProvider";
import { getDataProps } from "../../../shared/utilities";
import clsx from "clsx";

interface ModalProps {
    name?: string,
    title: string,
    visibilityParameter: string,
    maxWidth?: false | Breakpoint,
    fullscreen?: boolean;
    className?: string;
    onDismiss?: () => void,
    children: any,
}

const Modal: React.FC<ModalProps> = React.memo(function Modal(props) {
    const { title, visibilityParameter, onDismiss, maxWidth, children, fullscreen, className, ...rest } = props;
    const parameterValues = useAppSelector(state => state.storyline.parameterValues);
    const { updateParameterValue } = useAppActions();
    const [isOpen, setIsOpen] = React.useState(false);
    const dialogRef = React.useRef(null);

    React.useEffect(() => {
        const parameterValue = parameterValues.get(visibilityParameter);
        setIsOpen(!!parameterValue);
    }, [parameterValues, visibilityParameter]);

    const closeDialog = () => {
        setIsOpen(false);
        updateParameterValue(visibilityParameter, false);
        onDismiss?.();
    }

    return (
        <Dialog
            ref={dialogRef}
            open={isOpen}
            maxWidth={maxWidth !== undefined ? maxWidth : (fullscreen ? false : "md")}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') {
                    closeDialog();
                }
            }}
            aria-labelledby="form-dialog-title"
            disablePortal={true}
            className={clsx(fullscreen && "fullscreen-dialog", className)}
            {...rest}
            {...getDataProps(props)}>
            <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                {title}
            </DialogTitleWithCloseButton>
            {
                dialogRef.current ?
                    <TooltipContainerContext.Provider value={dialogRef}>{children}</TooltipContainerContext.Provider> :
                    <>{children}</>
            }
        </Dialog>
    );
});

(Modal as DocumentedComponent).metadata = {
    description: "The `Modal` component serves as a pop-up window, which can contain any other components as nested children.  Visibility of the modal is determined by the value of the specified parameter.",
    isSelfClosing: false,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "title", type: "string", description: "The text to display in the heading of the modal dialog." },
        { name: "visibilityParameter", type: "string", description: `The name of the parameter used to determine the visibility of the Modal.  Any _truthy_ value is interpreted as "visible" and any _falsey_ value is interpreted as "hidden".` },
        { name: "maxWidth", type: "string", exactType: "'xs' | 'sm' | 'md' | 'lg' | 'xl' | false", options: [`xs`, `sm`, `md`, `lg`, `xl`], description: "The maximum width of the modal.  Setting this value to `false` will disable the maximum width limitation.  Optional - defaults to `\"md\"`." },
        { name: "fullscreen", type: "boolean", description: "If true, the modal will take up the full width and height of the viewport (minus some padding).  Can be combined with `maxWidth` to constrain the dialog's width.  Optional - defaults to `false`." },
        { name: "onDismiss", type: "function", exactType: "() => void", template: "onDismiss={() => {$1}}", description: "The (optional) callback function to be invoked when the modal is dismissed by the user (backdrop or close button clicked on)."},
    ]
};

export default Modal;